import React, { useState, useMemo, useEffect } from "react";
import {
    groupBy,
    map,
    filter,
    union,
    without,
    find,
    every,
    contains,
    intersection,
} from "underscore";
import { setAdvancedFilters } from "@ts/revstack/helpers";
import { Button, Card, Checkbox, Divider, Tag } from "antd";
import { arraysEqual, titleCase } from "@ts/utils";
import { buildWidgetCardFilters } from "@/ts/revstack/ui-utils";
import { applyCurrentFiltersWithHash } from "@ts/revstack/filters";
import { useDispatch, useSelector } from "react-redux";
import {
    resetRevstackDashboardWidgetData,
    updateMainFilters,
    updateAiInsightsFilters,
    resetAllAdditionalFilters,
} from "@/features/revstackWidgetsSlice";
import { RevstackFilterTypes } from "@ts/common-types";
import { FilterInternals } from "./FilterInternals";

export enum FilterTypes {
    CATEGORIES = "categories",
    SUBCATEGORIES = "subcategories",
    BRANDS = "brands",
}

export enum BrandTypes {
    MY_BRAND = "mybrand",
    COMPETITION = "competition",
}

export const ALL_FILTER_KEYS = {
    UNIVERSAL: 'ALL_UNIVERSAL',
    COMPETITION: 'ALL_COMPETITION',
    CATEGORY: (categoryId: number) => `ALL_CATEGORY_${categoryId}`,
} as const;

export type FilterValue = number | string;

export const isAllFilter = (value: FilterValue): boolean => 
    typeof value === 'string' && value.startsWith('ALL_');

export const isCompetitionAll = (value: FilterValue): boolean => 
    value === ALL_FILTER_KEYS.COMPETITION;

export const isUniversalAll = (value: FilterValue): boolean => 
    value === ALL_FILTER_KEYS.UNIVERSAL;

export const isCategoryAll = (value: FilterValue): boolean => 
    typeof value === 'string' && value.startsWith('ALL_CATEGORY_');

export const getCategoryFromCategoryAll = (value: FilterValue): number => {
    if (typeof value === 'string' && value.startsWith('ALL_CATEGORY_')) {
        return parseInt(value.replace('ALL_CATEGORY_', ''));
    }
    return 0;
};

export const createCategoryAllKey = (categoryId: number): string => 
    ALL_FILTER_KEYS.CATEGORY(categoryId);

interface FilterOption {
    value: FilterValue;
    label: string;
    category?: number;
    subcategory?: number;
    subcategories?: number[];
    brandType?: string;
}

interface ITopLevelFiltersProps {
    mainFilters: any;
    expanded: boolean;
    setExpanded: (value: boolean) => void;
    activeTab: FilterTypes;
    setActiveTab: (value: FilterTypes) => void;
    search: string;
    setSearch: (value: string) => void;
    resetSearchInAllTabs: () => void;
    brandType: string;
    setBrandType: (value: string) => void;
    hideFilterPopover: boolean;
    draftSelected: any;
    setDraftSelected: (value: any) => void;
    appliedSelected: any;
    setAppliedSelected: (value: any) => void;
    filtersCloseHandler: () => void;
}

const TopLevelFilters: React.FC<ITopLevelFiltersProps> = ({
    mainFilters,
    expanded,
    setExpanded,
    activeTab,
    setActiveTab,
    search,
    setSearch,
    resetSearchInAllTabs,
    brandType,
    setBrandType,
    filtersCloseHandler,
    hideFilterPopover,
    draftSelected,
    setDraftSelected,
    appliedSelected,
    setAppliedSelected,
}) => {
    const dispatch = useDispatch();
    const mainFiltersData = useSelector(
        (state: any) => state.mainFiltersData.value
    );
    const revstackChannel = useSelector(
        (state: any) => state.revstackChannel.value
    );
    const revstackChannelsList = useSelector(
        (state: any) => state.revstackChannelsList.value
    );
    const selectedDateRange = useSelector(
        (state: any) => state.selectedDateRange.value
    );
    const toggle = useSelector(
        (state: any) => state.toggle.value
    );

    const [showMoreTags, setShowMoreTags] = useState({
        categories: false,
        subcategories: false,
        brands: false,
    });
    const [previousBrandType, setPreviousBrandType] = useState(brandType);
    const MAX_TAGS = 4;

    useEffect(() => {
        setDraftSelected(mainFilters);
        setAppliedSelected(mainFilters);
    }, [mainFilters]);

    useEffect(() => {
        if (activeTab !== FilterTypes.BRANDS) {
            setBrandType(BrandTypes.MY_BRAND);
        }
    }, [activeTab, setBrandType]);

    const currentSelected = useMemo(() => draftSelected, [draftSelected]);



    const handleBrandTypeChange = (newBrandType: string) => {
        setPreviousBrandType(brandType);
        setBrandType(newBrandType);
    };

    const getAllKey = (type: FilterTypes, currentBrandType: string = brandType): FilterValue => {
        if (type === FilterTypes.BRANDS && currentBrandType === BrandTypes.COMPETITION) {
            return ALL_FILTER_KEYS.COMPETITION;
        }
        return ALL_FILTER_KEYS.UNIVERSAL; // Default All key for all other cases
    };

    const sortOptionsAlphabetically = (options: FilterOption[]): FilterOption[] => {
        return [...options].sort((a, b) => a.label.localeCompare(b.label));
    };

    const sortCategoryIdsAlphabetically = (categoryIds: number[]): number[] => {
        return categoryIds.sort((a, b) => {
            const categoryA = mainFiltersData.categories.find(
                (cat: FilterOption) => cat.value === a
            )?.label || a.toString();
            const categoryB = mainFiltersData.categories.find(
                (cat: FilterOption) => cat.value === b
            )?.label || b.toString();
            return categoryA.localeCompare(categoryB);
        });
    };

    // Helper function to get names from filter IDs
    const getFilterNames = (
        filterIds: number[],
        filterOptions: FilterOption[]
    ): string[] => {
        return filterIds
            .filter((id: number) => id > 0)
            .map((id: number) => {
                const option = filterOptions.find(
                    (opt: FilterOption) => opt.value === id
                );
                return option?.label || "";
            })
            .filter(Boolean);
    };

    useEffect(() => {
        if (mainFiltersData && Object.keys(mainFiltersData).length > 0) {
            // Get the names of selected categories, subcategories, and brands using the helper function
            const categoryList = getFilterNames(
                currentSelected.categories,
                mainFiltersData.categories
            );
            const subcategoryList = getFilterNames(
                currentSelected.subcategories,
                mainFiltersData.subcategories
            );
            const brandList = getFilterNames(
                currentSelected.brands,
                mainFiltersData.brands
            );

            // Get channel name from revstackChannelsList using the selected channel ID
            const selectedChannelId = revstackChannel[0];
            const selectedChannel = revstackChannelsList.find(
                (channel: any) => channel.id === selectedChannelId
            );
            const channelName = selectedChannel
                ? selectedChannel.name
                : selectedChannelId
                    ? selectedChannelId.toString()
                    : "";

            const dateConfig = {
                current_period: selectedDateRange?.dateRange
                    ? [
                        selectedDateRange.dateRange.start,
                        selectedDateRange.dateRange.end,
                    ]
                    : [],
                previous_period: selectedDateRange?.compareDateRange
                    ? [
                        selectedDateRange.compareDateRange.start,
                        selectedDateRange.compareDateRange.end,
                    ]
                    : [],
            };

            dispatch(
                updateAiInsightsFilters({
                    categoryList,
                    subcategoryList,
                    brandList,
                    channel: channelName,
                    dateConfig,
                })
            );
        }
    }, [mainFiltersData, currentSelected, dispatch]);

    const tagsData = useMemo(() => {
        // Filter out ALL_* markers - only show numeric IDs in tags
        const selectedCategories = currentSelected.categories.filter(
            (id: FilterValue) => typeof id === 'number'
        );
        const selectedSubcategories = currentSelected.subcategories.filter(
            (id: FilterValue) => typeof id === 'number'
        );
        const selectedBrands = currentSelected.brands.filter(
            (id: FilterValue) => typeof id === 'number'
        );
        
        const optionsCategories = mainFiltersData.categories;
        const optionsSubcategories = mainFiltersData.subcategories;
        const optionsBrands = mainFiltersData.brands;

        return {
            categories: {
                tags: showMoreTags.categories
                    ? selectedCategories
                    : selectedCategories.slice(0, MAX_TAGS),
                remaining: selectedCategories.length - MAX_TAGS,
                options: optionsCategories,
            },
            subcategories: {
                tags: showMoreTags.subcategories
                    ? selectedSubcategories
                    : selectedSubcategories.slice(0, MAX_TAGS),
                remaining: selectedSubcategories.length - MAX_TAGS,
                options: optionsSubcategories,
            },
            brands: {
                tags: showMoreTags.brands
                    ? selectedBrands
                    : selectedBrands.slice(0, MAX_TAGS),
                remaining: selectedBrands.length - MAX_TAGS,
                options: optionsBrands,
            },
        };
    }, [currentSelected, showMoreTags, mainFiltersData]);

    const disableApplyButton = useMemo(() => {
        // Get actual numeric selections (exclude ALL_* markers)
        const actualCategories = currentSelected?.categories?.filter(
            (id: FilterValue) => typeof id === 'number'
        ) || [];
        
        const actualSubcategories = currentSelected?.subcategories?.filter(
            (id: FilterValue) => typeof id === 'number'
        ) || [];
        
        const actualBrands = currentSelected?.brands?.filter(
            (id: FilterValue) => typeof id === 'number'
        ) || [];

        // Check if at least one of each type is selected
        if (actualCategories.length === 0) {
            return true;
        }

        if (actualSubcategories.length === 0) {
            return true;
        }

        if (actualBrands.length === 0) {
            return true;
        }

        // Check if each selected category has at least one subcategory selected
        const selectedCategories = currentSelected?.categories || [];
        const selectedSubcategories = currentSelected?.subcategories || [];

        for (const categoryId of selectedCategories) {
            // Skip the "All" category
            if (isUniversalAll(categoryId)) continue;

            // Get all subcategories for this category
            const categorySubcategories = map(
                filter(
                    mainFiltersData.subcategories,
                    (sub: FilterOption) => sub.category === categoryId
                ),
                "value"
            );

            // Check if at least one subcategory from this category is selected
            const hasSelectedSubcategory = categorySubcategories.some(
                (subId: number) => selectedSubcategories.includes(subId)
            );

            if (!hasSelectedSubcategory) {
                return true;
            }
        }

        // Check if no new selection is made
        const appliedCategories = appliedSelected?.categories?.filter(
            (id: FilterValue) => typeof id === 'number'
        ) || [];
        
        const appliedSubcategories = appliedSelected?.subcategories?.filter(
            (id: FilterValue) => typeof id === 'number'
        ) || [];
        
        const appliedBrands = appliedSelected?.brands?.filter(
            (id: FilterValue) => typeof id === 'number'
        ) || [];

        // If current selection is the same as applied selection, disable the button
        const categoriesUnchanged = arraysEqual(actualCategories, appliedCategories);
        const subcategoriesUnchanged = arraysEqual(actualSubcategories, appliedSubcategories);
        const brandsUnchanged = arraysEqual(actualBrands, appliedBrands);

        if (categoriesUnchanged && subcategoriesUnchanged && brandsUnchanged) {
            return true;
        }

        return false;
    }, [currentSelected, appliedSelected]);

    useEffect(() => {
        setDraftSelected((prev: any) => {
            const updated = { ...prev };
            let hasChanges = false;

            const getCategorySubcategories = (categoryId: number) =>
                map(
                    filter(
                        mainFiltersData.subcategories,
                        (sub: FilterOption) => sub.category === categoryId
                    ),
                    "value"
                );

            // Filter to only numeric category IDs (exclude 'ALL_UNIVERSAL' etc)
            const selectedCategoryIds = updated.categories.filter(
                (id: FilterValue) => typeof id === 'number'
            );

            selectedCategoryIds.forEach((categoryId: number) => {
                const categorySubcategories =
                    getCategorySubcategories(categoryId);
                const allCategoryKey = createCategoryAllKey(categoryId);

                if (categorySubcategories.length === 0) return;

                const selectedSubsForThisCat = intersection(
                    updated.subcategories,
                    categorySubcategories
                );
                const allInCategorySelected =
                    selectedSubsForThisCat.length ===
                    categorySubcategories.length;

                if (
                    allInCategorySelected &&
                    !contains(updated.subcategories, allCategoryKey)
                ) {
                    updated.subcategories = union(updated.subcategories, [
                        allCategoryKey,
                    ]);
                    hasChanges = true;
                } else if (
                    !allInCategorySelected &&
                    contains(updated.subcategories, allCategoryKey)
                ) {
                    updated.subcategories = without(
                        updated.subcategories,
                        allCategoryKey
                    );
                    hasChanges = true;
                }
            });

            // Handle brand "All" logic for both brand types
            [BrandTypes.MY_BRAND, BrandTypes.COMPETITION].forEach((currentBrandType) => {
                const relevantBrands = filter(
                    mainFiltersData.brands,
                    (brand: FilterOption) =>
                        brand.brandType === currentBrandType &&
                        brand.subcategories?.some((subId) =>
                            updated.subcategories.includes(subId)
                        )
                );

                const brandValues = map(relevantBrands, "value");
                const selectedBrands = updated.brands || [];
                const allKey = getAllKey(FilterTypes.BRANDS, currentBrandType);

                const allSelected = brandValues.length > 0 &&
                    every(brandValues, (id: number) => contains(selectedBrands, id));

                if (allSelected && !contains(selectedBrands, allKey)) {
                    updated.brands = union(updated.brands, [allKey]);
                    hasChanges = true;
                } else if (!allSelected && contains(selectedBrands, allKey)) {
                    updated.brands = without(updated.brands, allKey);
                    hasChanges = true;
                }
            });

            return hasChanges ? updated : prev;
        });
    }, [
        mainFiltersData[FilterTypes.SUBCATEGORIES],
        mainFiltersData[FilterTypes.BRANDS],
        draftSelected[FilterTypes.CATEGORIES],  
        draftSelected[FilterTypes.SUBCATEGORIES],
        brandType,
    ]);

    const getAllowedSubcategories = (selectedCategories: FilterValue[]) => {
        // Filter to only numeric category IDs
        const numericCategories = selectedCategories.filter(
            (id: FilterValue) => typeof id === 'number'
        );
        
        return map(
            filter(mainFiltersData[FilterTypes.SUBCATEGORIES], (sub: FilterOption) =>
                contains(numericCategories, sub.category!)
            ),
            "value"
        );
    };

    const getAllowedBrands = (selectedSubcategories: number[]) => {
        return map(
            filter(
                mainFiltersData[FilterTypes.BRANDS],
                (brand: FilterOption) =>
                    brand.subcategories?.some((subId) =>
                        selectedSubcategories.includes(subId)
                    ) && brand.brandType === brandType
            ),
            "value"
        );
    };

    const updateCascadingSelections = (updated: any, type: string, isAdding: boolean) => {
        if (type === FilterTypes.CATEGORIES) {
            const allowedSub = getAllowedSubcategories(updated.categories);
            updated.subcategories = isAdding 
                ? union(updated.subcategories, allowedSub)
                : intersection(updated.subcategories, allowedSub);
            
            const allowedBrands = getAllowedBrands(updated.subcategories);
            updated.brands = isAdding
                ? union(updated.brands, allowedBrands)
                : intersection(updated.brands, allowedBrands);
        } else if (type === FilterTypes.SUBCATEGORIES) {
            const allowedBrands = getAllowedBrands(updated.subcategories);
            updated.brands = isAdding
                ? union(updated.brands, allowedBrands)
                : intersection(updated.brands, allowedBrands);
        }
        return updated;
    };

    const handleReset = () => {
        setSearch("");
        setDraftSelected((prev: any) => {
            const updated = { ...prev };
            updated[activeTab] = [];
            return updateCascadingSelections(updated, activeTab, false);
        });
    };

    const handleCheckboxChange = (
        type: FilterTypes,
        value: FilterValue
    ) => {
        setDraftSelected((prev: any) => {
            const already = prev[type].includes(value);
            const updated = { ...prev };

            const getCategorySubcategories = (categoryId: number) =>
                map(
                    filter(
                        mainFiltersData[FilterTypes.SUBCATEGORIES],
                        (sub: FilterOption) => sub.category === categoryId
                    ),
                    "value"
                );

            if (isUniversalAll(value)) {
                updated[type] = already
                    ? []
                    : union(
                        prev[type],
                        map(getFilteredOptions(type as FilterTypes), "value"),
                        [ALL_FILTER_KEYS.UNIVERSAL]
                    );
            } else if (type === FilterTypes.BRANDS && (isUniversalAll(value) || isCompetitionAll(value))) {
                // Handle brand-specific "All" selection
                const targetBrandType = isCompetitionAll(value) ? BrandTypes.COMPETITION : BrandTypes.MY_BRAND;
                const brandOptions = filter(
                    mainFiltersData[FilterTypes.BRANDS],
                    (brand: FilterOption) =>
                        brand.brandType === targetBrandType &&
                        brand.subcategories?.some((subId) =>
                            updated.subcategories.includes(subId)
                        )
                );
                const brandValues = map(brandOptions, "value");
                
                updated[type] = already
                    ? without(prev[type], ...brandValues, value)
                    : union(prev[type], brandValues, [value]);
            } else if (isCategoryAll(value) && type === FilterTypes.SUBCATEGORIES) {
                const categoryId = getCategoryFromCategoryAll(value);
                const categorySubcategories =
                    getCategorySubcategories(categoryId);
                updated[type] = already
                    ? without(prev[type], ...categorySubcategories, value)
                    : union(prev[type], categorySubcategories, [value]);
            } else {
                updated[type] = already
                    ? without(prev[type], value)
                    : union(prev[type], [value]);

                if (type === FilterTypes.SUBCATEGORIES) {
                    const currentOption = find(mainFiltersData[FilterTypes.SUBCATEGORIES], {
                        value,
                    });
                    if (currentOption) {
                        const categoryId = currentOption.category;
                        const categorySubcategories = getCategorySubcategories(
                            categoryId!
                        );
                        const allCategoryKey = createCategoryAllKey(categoryId!);
                        const allInCategorySelected = every(
                            categorySubcategories,
                            (sub: number) => contains(updated[type], sub)
                        );

                        updated[type] =
                            allInCategorySelected &&
                                !contains(updated[type], allCategoryKey)
                                ? union(updated[type], [allCategoryKey])
                                : !allInCategorySelected
                                    ? without(updated[type], allCategoryKey)
                                    : updated[type];
                    }
                    
                    // Only update cascading selections (brands) for subcategories
                    // Don't call updateCascadingSelections separately for individual subcategory changes
                    const allowedBrands = getAllowedBrands(updated.subcategories);
                    updated.brands = already
                        ? intersection(updated.brands, allowedBrands)
                        : union(updated.brands, allowedBrands);
                        
                    return updated; // Early return to skip the general cascading call
                } else if (type === FilterTypes.CATEGORIES) {
                    const allCategories = map(
                        mainFiltersData[FilterTypes.CATEGORIES],
                        "value"
                    );
                    const allSelected = every(allCategories, (opt: number) =>
                        contains(updated[type], opt)
                    );
                    updated[type] =
                        allSelected && !contains(updated[type], ALL_FILTER_KEYS.UNIVERSAL)
                            ? union(updated[type], [ALL_FILTER_KEYS.UNIVERSAL])
                            : !allSelected
                                ? without(updated[type], ALL_FILTER_KEYS.UNIVERSAL)
                                : updated[type];
                } else if (type === FilterTypes.BRANDS) {
                    // Update the appropriate "All" key for current brand type
                    const currentAllKey = getAllKey(FilterTypes.BRANDS, brandType);
                    const currentBrands = map(
                        filter(
                            mainFiltersData[FilterTypes.BRANDS],
                            (brand: FilterOption) =>
                                brand.subcategories?.some((subId) =>
                                    updated.subcategories.includes(subId)
                                ) && brand.brandType === brandType
                        ),
                        "value"
                    );
                    const allSelected = every(currentBrands, (opt: number) =>
                        contains(updated[type], opt)
                    );
                    
                    updated[type] = allSelected && !contains(updated[type], currentAllKey)
                        ? union(updated[type], [currentAllKey])
                        : !allSelected
                            ? without(updated[type], currentAllKey)
                            : updated[type];
                }
            }

            updateCascadingSelections(updated, type, !already);

            return updated;
        });
    };

    const renderSelectedTags = (
        type: FilterTypes
    ) => {
        const { tags, remaining, options } = tagsData[type];

        return (
            <div className="filter-pills-section">
                {tags.map((val: number) => {
                    const opt = options.find(
                        (o: FilterOption) => o.value === val
                    );
                    if (!opt) return null;
                    return (
                        <Tag
                            key={val}
                            className="filter-tags"
                            closable
                            onClose={() => handleCheckboxChange(type, val)}
                        >
                            {opt.label}
                        </Tag>
                    );
                })}
                {remaining > 0 && (
                    <span
                        className="filter-tags-more-btn"
                        onClick={() =>
                            setShowMoreTags((prev) => ({
                                ...prev,
                                [type]: !prev[type],
                            }))
                        }
                    >
                        {showMoreTags[type] ? `- Show Less` : `+ Show More`}
                    </span>
                )}
            </div>
        );
    };

    const getFilteredOptions = (
        type: FilterTypes
    ): FilterOption[] => {
        let options = mainFiltersData[type];
        if (type === FilterTypes.SUBCATEGORIES) {
            // Filter to only numeric category IDs
            const numericCategories = currentSelected.categories.filter(
                (id: FilterValue) => typeof id === 'number'
            );
            options = options.filter(
                (opt: FilterOption) =>
                    "category" in opt &&
                    numericCategories.includes(opt.category!)
            );
        }
        if (type === FilterTypes.BRANDS) {
            options = options.filter(
                (opt: FilterOption) =>
                    opt.brandType === brandType &&
                    opt.subcategories?.some((subId) =>
                        currentSelected.subcategories.includes(subId)
                    )
            );
        }
        return options.filter((opt: FilterOption) =>
            opt.label.toLowerCase().includes(search.toLowerCase())
        );
    };

    const renderCheckbox = (
        opt: FilterOption,
        type: FilterTypes
    ) => (
        <Checkbox
            key={opt.value}
            className="filter-checkbox"
            checked={currentSelected[type].includes(opt.value)}
            onChange={() => handleCheckboxChange(type, opt.value)}
        >
            &nbsp;{opt.label}
        </Checkbox>
    );

    const renderAllCheckbox = (
        key: FilterValue,
        type: FilterTypes
    ) => {
        // Use the key passed as parameter (for category-specific All in subcategories)
        // or get the appropriate All key for categories/brands
        const actualKey = key;
        
        return (
            <Checkbox
                key={actualKey}
                className="all-filter-checkbox"
                checked={currentSelected[type].includes(actualKey)}
                onChange={() => handleCheckboxChange(type, actualKey)}
            >
                &nbsp;All
            </Checkbox>
        );
    };

    const getAllAvailableOptions = (
        type: FilterTypes
    ) => {
        let options = mainFiltersData[type];
        if (type === FilterTypes.SUBCATEGORIES) {
            // Filter to only numeric category IDs
            const numericCategories = currentSelected.categories.filter(
                (id: FilterValue) => typeof id === 'number'
            );
            options = options.filter(
                (opt: FilterOption) =>
                    "category" in opt &&
                    numericCategories.includes(opt.category!)
            );
        }
        if (type === FilterTypes.BRANDS) {
            options = options.filter(
                (opt: FilterOption) =>
                    opt.brandType === brandType &&
                    opt.subcategories?.some((subId) =>
                        currentSelected.subcategories.includes(subId)
                    )
            );
        }
        return options;
    };

    //! Do not remove this fn to be used for intial load
    const sortOptionsByCheckedAndLabel = (
        options: FilterOption[],
        type: FilterTypes
    ) => {
        return [...options].sort((a, b) => {
            const aChecked = currentSelected[type].includes(a.value);
            const bChecked = currentSelected[type].includes(b.value);

            if (aChecked && !bChecked) return -1;
            if (!aChecked && bChecked) return 1;

            return a.label.localeCompare(b.label);
        });
    };

    const filterOptionsBySearch = (
        options: FilterOption[],
        searchTerm: string
    ) => {
        return searchTerm
            ? options.filter((opt) =>
                opt.label.toLowerCase().includes(searchTerm.toLowerCase())
            )
            : options;
    };

    const renderNoOptionsMessage = (
        type: FilterTypes
    ) => {
        const typeLabels = {
            [FilterTypes.CATEGORIES]: "Categories",
            [FilterTypes.SUBCATEGORIES]: "Sub Categories",
            [FilterTypes.BRANDS]: "Brands",
        };

        const brandTypeLabel = {
            [BrandTypes.MY_BRAND]: "Selected Brands",
            [BrandTypes.COMPETITION]: "Competition",
        };

        return (
            <div
                className="no-options-message"
                style={{
                    textAlign: "center",
                    padding: "20px",
                    color: "#666",
                    fontStyle: "italic",
                }}
            >
                No{" "}
                {type === FilterTypes.BRANDS
                    ? brandTypeLabel[brandType as keyof typeof brandTypeLabel]
                    : typeLabels[type]}{" "}
                available to select
            </div>
        );
    };

    const renderSimpleCheckboxGrid = (
        options: FilterOption[],
        type: FilterTypes
    ) => {
        const sortedOptions = sortOptionsAlphabetically(options);
        
        // Don't show "All" checkbox for competition brand filters
        const shouldShowAllCheckbox = !(type === FilterTypes.BRANDS && brandType === BrandTypes.COMPETITION);
        
        return (
            <div className="filter-checkbox-grid overflow-auto" style={{ maxHeight: "250px", gap: "12px 16px" }}>
                {sortedOptions.length > 0 ? (
                    <>
                        {shouldShowAllCheckbox && renderAllCheckbox(ALL_FILTER_KEYS.UNIVERSAL, type)}
                        {sortedOptions.map((opt: FilterOption) =>
                            renderCheckbox(opt, type)
                        )}
                    </>
                ) : (
                    renderNoOptionsMessage(type)
                )}
            </div>
        );
    };

    const renderSimpleCheckboxGridWithoutAll = (
        options: FilterOption[],
        type: FilterTypes
    ) => (
        <div className="filter-checkbox-grid overflow-auto" style={{ maxHeight: "250px", gap: "12px 16px" }}>
            {options.length > 0
                ? options.map((opt: FilterOption) => renderCheckbox(opt, type))
                : renderNoOptionsMessage(type)}
        </div>
    );

    const renderCategoryGroup = (
        categoryId: number,
        categoryOptions: FilterOption[],
        type: FilterTypes
    ) => {
        const categoryLabel =
            mainFiltersData.categories.find(
                (cat: FilterOption) => cat.value === categoryId
            )?.label || categoryId.toString();

        const displayOptions = filterOptionsBySearch(categoryOptions, search);
        const sortedDisplayOptions = sortOptionsAlphabetically(displayOptions);

        if (sortedDisplayOptions.length === 0 && search) {
            return null;
        }

        return (
            <div key={categoryId} className="mb-3">
                <div className="filter-checkbox-grid-category">
                    {categoryLabel}
                </div>
                <div className="filter-checkbox-grid" style={{ maxHeight: "none", gap: "12px 16px" }}>
                    {sortedDisplayOptions.length > 0 ? (
                        <>
                            {renderAllCheckbox(createCategoryAllKey(categoryId), type)}
                            {sortedDisplayOptions.map((opt: FilterOption) =>
                                renderCheckbox(opt, type)
                            )}
                        </>
                    ) : (
                        renderNoOptionsMessage(type)
                    )}
                </div>
            </div>
        );
    };

    const renderCheckboxGrid = (
        type: FilterTypes
    ) => {
        const allAvailableOptions = getAllAvailableOptions(type);

        if (type === FilterTypes.CATEGORIES) {
            const displayOptions = filterOptionsBySearch(
                allAvailableOptions,
                search
            );

            return renderSimpleCheckboxGrid(displayOptions, type);
        }

        if (type === FilterTypes.BRANDS) {
            const displayOptions = filterOptionsBySearch(
                allAvailableOptions,
                search
            );

            return renderSimpleCheckboxGrid(displayOptions, type);
        }

        const categoryGroups = groupBy(
            allAvailableOptions,
            (opt: FilterOption) => opt.category?.toString() || ""
        );
        
        const categoryIds = sortCategoryIdsAlphabetically(
            Object.keys(categoryGroups).map((id) => parseInt(id))
        );

        // For subcategories, show no options message if no categories are selected
        if (type === FilterTypes.SUBCATEGORIES && categoryIds.length === 0) {
            return renderNoOptionsMessage(type);
        }

        // Check if any category group has search results
        const hasSearchResults = categoryIds.some((categoryId) => {
            const categoryOptions = categoryGroups[categoryId.toString()];
            const displayOptions = filterOptionsBySearch(
                categoryOptions,
                search
            );
            return displayOptions.length > 0;
        });

        // If no search results found in any category, show no options message
        if (search && !hasSearchResults) {
            return renderNoOptionsMessage(type);
        }

        return (
            <div className="overflow-auto" style={{ maxHeight: "250px" }}>
                {categoryIds.map((categoryId) =>
                    renderCategoryGroup(
                        categoryId,
                        categoryGroups[categoryId.toString()],
                        type
                    )
                )}
            </div>
        );
    };

    const getActualSelectedCount = (
        type: FilterTypes
    ) => {
        const selectedItems = currentSelected[type] || [];
        if (type === FilterTypes.BRANDS) {
            // Exclude All keys from count
            return selectedItems.filter((item: FilterValue) => 
                typeof item === 'number' && item > 0 && !isAllFilter(item)
            ).length;
        }
        return selectedItems.filter((item: FilterValue) => 
            typeof item === 'number' && !isAllFilter(item)
        ).length;
    };

    const filterSummarybar = () => {
        return (
            <div
                className="filter-summary-tab-container shadow-sm py-2"
                role="button"
                onClick={() => setExpanded(true)}
            >
                <div className="filter-summary-tab">
                    {getActualSelectedCount(FilterTypes.CATEGORIES)} Categories Selected
                </div>
                <Divider
                    type="vertical"
                    className="filter-summary-tab-divider"
                />
                <div className="filter-summary-tab">
                    {getActualSelectedCount(FilterTypes.SUBCATEGORIES)} Sub Categories
                    Selected
                </div>
                <Divider
                    type="vertical"
                    className="filter-summary-tab-divider"
                />
                <div className="filter-summary-tab">
                    {getActualSelectedCount(FilterTypes.BRANDS)} Brands Selected
                </div>
                <Button className="filter-apply-button disabled me-2" disabled>
                    Apply
                </Button>
            </div>
        );
    };

    const filterExpandedBar = () => (
        <div
            className={`filter-parent-container w-100 ${activeTab === FilterTypes.CATEGORIES
                ? "active-categories"
                : activeTab === FilterTypes.SUBCATEGORIES
                    ? "active-subcategories"
                    : "active-brands"
                }`}
        >
            <div
                className={`filter-container ${activeTab === FilterTypes.CATEGORIES ? "active" : ""
                    }`}
                onClick={() => setActiveTab(FilterTypes.CATEGORIES)}
            >
                <div className="filter-container-inner">
                    <div className="select-label">CATEGORIES</div>
                    <div className="select-text">
                        {getActualSelectedCount(FilterTypes.CATEGORIES)} Selected
                    </div>
                </div>
            </div>
            <div
                    className={`filter-container ${activeTab === FilterTypes.SUBCATEGORIES ? "active" : ""
                    }`}
                onClick={() => setActiveTab(FilterTypes.SUBCATEGORIES)}
            >
                <div
                    className={`filter-container-inner ${activeTab === FilterTypes.BRANDS ? "left-border" : ""
                        }`}
                >
                    <div className="select-label">SUB CATEGORIES</div>
                    <div className="select-text">
                        {getActualSelectedCount(FilterTypes.SUBCATEGORIES)} Selected
                    </div>
                </div>
            </div>
            <div
                className={`filter-container ${activeTab === FilterTypes.BRANDS ? "active" : ""
                    }`}
                onClick={() => setActiveTab(FilterTypes.BRANDS)}
            >
                <div
                    className={`filter-container-inner ${activeTab === FilterTypes.CATEGORIES ? "left-border" : ""
                        }`}
                >
                    <div className="select-label">BRANDS</div>
                    <div className="select-text">
                        {getActualSelectedCount(FilterTypes.BRANDS)} Selected
                    </div>
                </div>
            </div>
            <Button
                className={`filter-apply-button ${disableApplyButton ? "expanded-apply-disabled" : ""
                    }`}
                disabled={disableApplyButton}
                onClick={handleApply}
            >
                Apply
            </Button>
        </div>
    );

    const handleApply = () => {
        dispatch(resetRevstackDashboardWidgetData());
        dispatch(resetAllAdditionalFilters());
        setAppliedSelected(draftSelected);

        dispatch(updateMainFilters({
            ...draftSelected,
            rerender: !mainFilters.rerender
        }));

        // Fetch advanced filters based on the new main filters
        setAdvancedFilters(
            draftSelected.categories,
            draftSelected.subcategories,
            draftSelected.brands
        );

        applyCurrentFiltersWithHash({
            mainFilters: draftSelected
        });

        filtersCloseHandler();
    };

    return (
        <div className="d-flex flex-column gap-3 position-relative">
            {expanded && !hideFilterPopover
                ? filterExpandedBar()
                : filterSummarybar()}
            {expanded && !hideFilterPopover && (
                <>
                    <div
                        className="filter-overlay"
                        style={{
                            left: toggle === "toggled" ? "295px" : "0px"
                        }}
                        onClick={filtersCloseHandler}
                    />
                    {toggle === "toggled" && (
                        <div
                            className="filter-sidebar-overlay"
                            onClick={filtersCloseHandler}
                        />
                    )}
                    <Card
                        className="filter-dropdown-container d-flex flex-column overflow-hidden"
                        styles={{
                            body: {
                                padding: 0,
                            },
                        }}
                        style={{ maxHeight: "70vh", display: "flex", flexDirection: "column" }}
                    >
                        <FilterInternals
                            selectedCount={getActualSelectedCount(activeTab as FilterTypes)}
                            totalCount={(mainFiltersData as any)?.[activeTab as FilterTypes]?.length || 0}
                            selectedTags={renderSelectedTags(activeTab as FilterTypes)}
                            searchPlaceholder={`Search for ${titleCase(activeTab as FilterTypes)}`}
                            searchValue={search}
                            onSearchChange={setSearch}
                            onSearchReset={handleReset}
                            headerExtraContent={
                                activeTab === FilterTypes.BRANDS ?
                                    buildWidgetCardFilters({
                                        filters: [
                                            {
                                                section: "" as const,
                                                type: RevstackFilterTypes.RADIO,
                                                props: {
                                                    options: [
                                                        {
                                                            label: "My Brand(s)",
                                                            value: BrandTypes.MY_BRAND,
                                                        },
                                                        {
                                                            label: "Competition",
                                                            value: BrandTypes.COMPETITION,
                                                        },
                                                    ],
                                                    onChange: (value: string) =>
                                                        handleBrandTypeChange(value),
                                                },
                                            },
                                        ]
                                    }) : undefined
                            }
                            mainContent={renderCheckboxGrid(activeTab as FilterTypes)}
                            contentPadding="px-3 py-4"
                        />
                    </Card>
                </>
            )}
        </div>
    );
};

export default TopLevelFilters;